/**
 * The Output pane's field list for the Call Script layout (BV-1575).
 *
 * Renders the "All fields" board — every output leaf grouped by schema section, with
 * status dots, click-to-heard, inline edit and its own All / Missed / Required filter —
 * in place of the read-oriented `FieldGroupAccordion` the pane shows for every other
 * call. The board and the script column share one workspace (see
 * `HumanCallWorkspaceContext`), so a value captured in either appears immediately in the
 * other and both agree on which fields are still missing.
 *
 * Disposition and Notes are NOT rendered here: they stay in the pane's frozen header
 * strip above this list, unchanged.
 *
 * Returns null when there is no provider — the caller only mounts this inside the Call
 * Script layout, and this keeps that contract explicit rather than crashing if it drifts.
 */
import React, { useEffect, useImperativeHandle, useRef } from 'react';

import { FieldBoard, type FieldBoardHandle } from './FieldBoard';
import { useHumanCallWorkspaceContext } from './HumanCallWorkspaceContext';

interface ReviewOutputFieldBoardProps {
  caseId: string;
  /** False for a non-assignee viewer or an already-dispositioned call. */
  editable: boolean;
  /** True only while a live human call is up — gates the mark-heard affordances. */
  callActive: boolean;
  /**
   * Save-time validation errors from the page (BV-2129), merged over the workspace's
   * live ones.
   *
   * Two stores, deliberately, because they answer different questions. The workspace
   * validates on write, so it only knows about fields touched this session. The page's
   * `collectPhoneFieldErrors` runs on Save across the WHOLE output, so it is the one
   * that can flag a bad value the rep never opened — and it is what the header banner
   * and its View action key off. Merging means the field the banner points at also shows
   * its message inline, instead of the two disagreeing.
   */
  fieldErrors?: Record<string, string>;
  /**
   * Publishes the WORKSPACE's live validation errors up to the page (LA-7358).
   *
   * The page owns the pane header, and therefore the "Validation errors found." banner
   * beside Save — but in this layout the errors live in the workspace, which the page
   * cannot read: it renders `HumanCallWorkspaceProvider`, so it sits above the context.
   * Without this the banner keyed off the page's own draft, which in this layout is
   * always clean, so a refused Save reported nothing at all.
   *
   * Must be referentially stable (a `useState` setter is) — it is an effect dependency.
   */
  onValidationErrorsChange?: (errors: Record<string, string>) => void;
  /** Forwarded to the board's title row — the Output sidebar's collapse control. */
  leadingActions?: React.ReactNode;
}

/** Imperative surface for the Call Script layout's Output pane (BV-2129). */
export interface ReviewOutputBoardHandle extends FieldBoardHandle {
  /**
   * Validate the whole working output, show the messages on the fields, and scroll to
   * the first failure. Returns true when something failed, so Save can bail.
   *
   * Lives here rather than on FieldBoard because the validation needs the WORKSPACE's
   * output — the draft the board and script actually write into — and only this wrapper
   * has it.
   */
  validateAndReveal: () => boolean;
  /**
   * Write top-level output keys (the disposition's flag set/reset) into the WORKSPACE's
   * draft as one revision, which its debounced save then persists (BV-2171).
   *
   * Save posts only the disposition in this layout. Its output flags must go through the
   * workspace instead: the workspace saves its whole working tree on every edit, so a
   * flag posted around it would be overwritten by the rep's next keystroke.
   */
  applyOutputPatch: (patch: Record<string, unknown>) => void;
}

const ReviewOutputFieldBoardComponent = React.forwardRef<
  ReviewOutputBoardHandle,
  ReviewOutputFieldBoardProps
>(function ReviewOutputFieldBoard(
  { caseId, editable, callActive, fieldErrors, onValidationErrorsChange, leadingActions },
  ref,
) {
  const workspace = useHumanCallWorkspaceContext();
  const boardRef = useRef<FieldBoardHandle | null>(null);

  // Read through the optional workspace so this runs BEFORE the `!workspace` early
  // return below — a hook after it would change hook order between renders.
  const liveFieldErrors = workspace?.fieldErrors;
  useEffect(() => {
    onValidationErrorsChange?.(liveFieldErrors ?? {});
  }, [liveFieldErrors, onValidationErrorsChange]);

  useImperativeHandle(
    ref,
    () => ({
      revealField: (key: string) => boardRef.current?.revealField(key),
      validateAndReveal: () => {
        const errors = workspace?.validateAllFields() ?? {};
        // collectPhoneFieldErrors inserts keys in schema document order, so the first is
        // the first failing field.
        const first = Object.keys(errors)[0];
        if (!first) return false;
        boardRef.current?.revealField(first);
        return true;
      },
      applyOutputPatch: (patch: Record<string, unknown>) =>
        workspace?.onFieldsChange(Object.entries(patch).map(([key, value]) => ({ key, value }))),
    }),
    [workspace],
  );

  if (!workspace) return null;

  // `fieldErrors` is deliberately not destructured here — it is already read above as
  // `liveFieldErrors`, before the early return, so the effect can publish it upward.
  const {
    board,
    heardAt,
    toggleHeard,
    onFieldChange,
    handleOutputCommit,
    callStartMs,
    filterMode,
    setFilterMode,
    revealInScript,
  } = workspace;

  return (
    <FieldBoard
      ref={boardRef}
      sections={board}
      fieldErrors={{ ...liveFieldErrors, ...fieldErrors }}
      readOnly={!editable}
      callActive={callActive}
      heardAt={heardAt}
      callStartMs={callStartMs}
      onFieldChange={onFieldChange}
      onFieldCommit={handleOutputCommit}
      toggleHeard={toggleHeard}
      caseRecordId={caseId}
      // The board owns the only All / Missed / Required control; the value lives on the
      // workspace so the Call Script narrows with it (LA-7358).
      filter={filterMode}
      onFilterChange={setFilterMode}
      // A row click also scrolls the Call Script column to the same field.
      onRowSelect={revealInScript}
      leadingActions={leadingActions}
    />
  );
});

export const ReviewOutputFieldBoard = ReviewOutputFieldBoardComponent;
